import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  getBlogPosts,
  deleteBlogPost,
  BlogPost,
} from "@/modules/blog/blogService";
import { auth } from "../../firebase/firebase";
import "../styles/BlogAdmin.css";
import LoadingSpinner from "@/components/LoadingSpinner";

export default function BlogAdmin() {
  const [user, loading] = useAuthState(auth);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const data = await getBlogPosts();
        setPosts(data);
      } catch (err) {
        console.error("Error fetching posts:", err);
      } finally {
        setPostsLoading(false);
      }
    };

    if (user) {
      fetchPosts();
    }
  }, [user]);

  const handleDelete = async (id: string) => {
    const confirmDelete = confirm("Are you sure you want to delete this blog post?");
    if (!confirmDelete) return;

    try {
      await deleteBlogPost(id);
      setPosts((prev) => prev.filter((post) => post.id !== id));
    } catch (err) {
      console.error("Error deleting post:", err);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Blog Admin</h1>
        <button onClick={() => navigate("/admin/blog/new")}>+ New Blog</button>
      </div>

      {postsLoading ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <LoadingSpinner />
        </div>
      ) : posts.length === 0 ? (
        <p>No blog posts found.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Slug</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {posts.map((post) => (
              <tr key={post.id}>
                <td>{post.title}</td>
                <td>{post.slug}</td>
                <td>{new Date(post.datePublished).toLocaleDateString()}</td>
                <td>
                  <button onClick={() => navigate(`/admin/blog/edit/${post.id}`)}>
                    Edit
                  </button>
                  <button onClick={() => handleDelete(post.id)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
